<template>
  <div>
    <div class="appeal-success" v-if="isSubmit">
      <h1 class="appeal-title">{{ $t("helpForm.titleSuccess") }}</h1>
      <h2 class="appeal-subtitle">{{ $t("helpForm.subtitleSuccess") }}</h2>
      <primary-button @click="onRepeat">{{
        $t("helpForm.submitAgain")
      }}</primary-button>
    </div>
    <div v-else>
      <h1 class="appeal-title">{{ $t("helpForm.title") }}</h1>
      <h2 class="appeal-subtitle">{{ $t("helpForm.subtitle") }}</h2>
      <form class="appeal-form" @submit.prevent="onSubmit">
        <div class="appeal-form-input">
          <input-label class="label">
            <template #label>
              {{ $t("helpForm.exhibition") }}
            </template>
            <Select
              :placeholder="$t('services.orders.statusSelectTitle')"
              :disabled="isExhibitionDisabled"
              v-model="form.exhibition"
              name="exhibition"
              v-validate="{
                required: true,
                single_dropdown_in: exhibitionsLocaled
              }"
              :options="exhibitionsLocaled"
              :search-count="20"
            />
            <template v-if="errors.has('exhibition')" #error>
              {{ errors.first("exhibition") }}
            </template>
          </input-label>
        </div>

        <div class="appeal-form-grid">
          <input-label class="label">
            <template #label>
              {{ $t("helpForm.pavilion") }}
            </template>
            <input
              name="pavilion"
              :disabled="isDisabled"
              v-model="form.pavilion"
              v-validate="'required'"
            />
            <template v-if="errors.has('pavilion')" #error>
              {{ errors.first("pavilion") }}
            </template>
          </input-label>
          <input-label class="label">
            <template #label>
              {{ $t("helpForm.stand") }}
            </template>
            <input
              name="stand"
              :disabled="isDisabled"
              v-model="form.stand"
              v-validate="'required'"
            />
            <template v-if="errors.has('stand')" #error>
              {{ errors.first("stand") }}
            </template>
          </input-label>
          <input-label class="label">
            <template #label>
              {{ $t("helpForm.name") }}
            </template>
            <input
              :disabled="isDisabled"
              v-model="form.name"
              name="name"
              v-validate="'required'"
            />
            <template v-if="errors.has('name')" #error>
              {{ errors.first("name") }}
            </template>
          </input-label>
          <input-label class="label">
            <template #label>
              {{ $t("helpForm.phone") }}
            </template>
            <input
              :disabled="isDisabled"
              v-model="form.phone"
              name="phone"
              v-validate="'required'"
            />
            <template v-if="errors.has('phone')" #error>
              {{ errors.first("phone") }}
            </template>
          </input-label>
        </div>
        <div class="appeal-form-input">
          <input-label class="label">
            <template #label>
              {{ $t("helpForm.problemCategory") }}
            </template>
            <Select
              :placeholder="$t('services.orders.statusSelectTitle')"
              :disabled="isDisabled"
              v-model="form.category"
              name="category"
              v-validate="{
                required: true,
                single_dropdown_in: categoriesLocaled
              }"
              :options="categoriesLocaled"
              :search-count="20"
            />
            <template v-if="errors.has('category')" #error>
              {{ errors.first("category") }}
            </template>
          </input-label>
        </div>

        <input-label class="label">
          <template #label>
            {{ $t("helpForm.infoText") }}
          </template>
          <textarea
            :disabled="isDisabled"
            v-model="form.description"
            class="textarea"
            name="description"
            v-validate="'required'"
          />
          <template v-if="errors.has('description')" #error>
            {{ errors.first("description") }}
          </template>
        </input-label>

        <input-label class="label-checkbox">
          <template #label>
            {{ $t("helpForm.privatePolicy") }}
            <a
              :href="
                `https://www.expocentr.ru/upload/docs/Expocentre_personal_data_processing_policy_${locale}.pdf`
              "
              target="_blank"
            >
              {{ $t("helpForm.privatePolicyLink") }}</a
            >
          </template>
          <checkbox v-model="isAgreePersonalData" />
        </input-label>
        <primary-button
          type="submit"
          :disabled="!isAgreePersonalData || isDisabled"
          >{{ $t("helpForm.submit") }}</primary-button
        >
      </form>
    </div>
  </div>
</template>

<script>
import {
  PrimaryButton,
  Checkbox,
  InputLabel,
  Select
} from "@v2/shared/ui/components";
import { postHelpForm } from "@v2/features/appeals/api";
import { i18n } from "@v2/shared/config";

export default {
  name: "AppealsForm",
  components: { Checkbox, Select, InputLabel, PrimaryButton },
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    exhibitions: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        pavilion: "",
        stand: "",
        name: "",
        phone: "",
        category: "",
        description: "",
        exhibition: ""
      },
      isAgreePersonalData: false,
      isSubmit: false,
      isDisabled: this.disabled
    };
  },
  methods: {
    onRepeat() {
      this.form.category = "";
      this.form.description = "";
      this.isAgreePersonalData = false;
      this.isSubmit = false;
    },
    async onSubmit() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;
      this.isDisabled = true;
      try {
        await postHelpForm(this.form);
        this.isSubmit = true;
      } catch (e) {
        await this.$store.dispatch(
          "common/setErrorMessage",
          this.$t("helpForm.error")
        );
      } finally {
        this.isDisabled = false;
      }
    }
  },
  computed: {
    locale() {
      return i18n.locale;
    },
    categoriesLocaled() {
      return this.categories.map(item => {
        return {
          value: item.id,
          title: this.locale === "ru" ? item.name_rus : item.name_eng
        };
      });
    },
    exhibitionsLocaled() {
      return this.exhibitions.map(item => {
        return {
          value: item.id,
          title: this.locale === "ru" ? item.name_rus : item.name_eng
        };
      });
    },
    isExhibitionDisabled() {
      return this.exhibitions.length === 1 || this.isDisabled;
    }
  },
  mounted() {
    if (this.exhibitions.length === 1) {
      this.form.exhibition = this.exhibitions[0].id;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
.appeal {
  &-title {
    @include h1();
    @include respond-down(md) {
      font-size: 28px;
    }
  }
  &-subtitle {
    color: var(--dark-light, #6d7179);
    font-size: 16px;
    padding: 10px 0;
    font-weight: 400;
  }
  &-form {
    & .label-checkbox {
      display: flex;
      flex-direction: row-reverse;
      justify-content: flex-end;
      padding: 40px 0 20px 0;
      text-align: left;
      & a {
        color: inherit;
      }
      & .input-label__labels {
        height: auto;
      }
    }
    &-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 40px 20px;
      margin-bottom: 40px;
      @include respond-down(md) {
        gap: 20px;
        margin-bottom: 20px;
      }
    }
    &-input {
      margin-bottom: 40px;

      @include respond-down(md) {
        margin-bottom: 20px;
      }
    }
    padding: 10px;
  }
  & .textarea {
    box-sizing: border-box;
    border: 1px solid #cdced0;
    border-radius: 2px;
    width: 100%;
    padding: 10px;
    resize: vertical;
    min-height: 50px;
    max-height: 500px;
    &:hover,
    &:focus {
      border-color: #a3a8b3;
      outline: none;
    }
  }
  &-success {
    display: flex;
    flex-direction: column;
    & .primary-button {
      align-self: center;
    }
  }
  & .primary-button {
    @include respond-down(sm) {
      width: 100%;
    }
  }
}
</style>
